import { create } from "zustand";
import { persist } from "zustand/middleware";
import { posthog, isPostHogEnabled } from "@/lib/posthog";

/**
 * Experiment keys as constants for type safety.
 * These should match the feature flag names in PostHog.
 */
export const EXPERIMENTS = {
  CTA_PLACEMENT: "cta-placement-experiment",
  CTA_COPY: "cta-copy-experiment",
  UPGRADE_FLOW: "upgrade-flow-experiment",
} as const;

export type ExperimentKey = (typeof EXPERIMENTS)[keyof typeof EXPERIMENTS];

/**
 * Variant types for each experiment.
 */
export type CtaPlacementVariant = "control" | "sidebar" | "dashboard" | "rides-page" | "all";
export type CtaCopyVariant = "control" | "action-focused" | "benefit-focused" | "urgency";
export type UpgradeFlowVariant = "control" | "simplified" | "comparison-table" | "guided";

interface ExperimentState {
  variants: Record<ExperimentKey, string | undefined>;
  isLoaded: boolean;

  // Actions
  setVariant: (experiment: ExperimentKey, variant: string) => void;
  loadVariants: () => Promise<void>;
  trackExperimentView: (experiment: ExperimentKey, location?: string) => void;
  trackConversion: (
    experiment: ExperimentKey,
    conversionType: string,
    properties?: Record<string, unknown>
  ) => void;
}

export const useExperimentStore = create<ExperimentState>()(
  persist(
    (set, get) => ({
      variants: {
        [EXPERIMENTS.CTA_PLACEMENT]: undefined,
        [EXPERIMENTS.CTA_COPY]: undefined,
        [EXPERIMENTS.UPGRADE_FLOW]: undefined,
      },
      isLoaded: false,

      setVariant: (experiment, variant) => {
        set((state) => ({
          variants: { ...state.variants, [experiment]: variant },
        }));
      },

      loadVariants: async () => {
        if (!isPostHogEnabled()) {
          // No PostHog - use defaults (control variants)
          set({ isLoaded: true });
          return;
        }

        try {
          // Wait for PostHog to load feature flags using onFeatureFlags callback
          await new Promise<void>((resolve) => {
            posthog.onFeatureFlags(() => {
              resolve();
            });
          });

          const variants: Record<ExperimentKey, string | undefined> = {
            [EXPERIMENTS.CTA_PLACEMENT]: posthog.getFeatureFlag(
              EXPERIMENTS.CTA_PLACEMENT
            ) as string | undefined,
            [EXPERIMENTS.CTA_COPY]: posthog.getFeatureFlag(
              EXPERIMENTS.CTA_COPY
            ) as string | undefined,
            [EXPERIMENTS.UPGRADE_FLOW]: posthog.getFeatureFlag(
              EXPERIMENTS.UPGRADE_FLOW
            ) as string | undefined,
          };

          set({ variants, isLoaded: true });
        } catch {
          // On error, just mark as loaded with existing/default variants
          set({ isLoaded: true });
        }
      },

      trackExperimentView: (experiment, location) => {
        if (!isPostHogEnabled()) return;

        const variant = get().variants[experiment];
        if (variant) {
          posthog.capture("$experiment_viewed", {
            experiment_key: experiment,
            variant,
            ...(location && { location }),
          });
        }
      },

      trackConversion: (experiment, conversionType, properties = {}) => {
        if (!isPostHogEnabled()) return;

        const variant = get().variants[experiment];
        if (variant) {
          posthog.capture("experiment_conversion", {
            experiment_key: experiment,
            variant,
            conversion_type: conversionType,
            ...properties,
          });
        }
      },
    }),
    {
      name: "experiment-storage",
      partialize: (state) => ({
        variants: state.variants,
      }),
    }
  )
);
